import type { ExpressionSpecification, GeoJSONSource, Map as MlMap } from 'maplibre-gl';
import type { Aircraft, Airport, ApproachProcedure, Bbox, CoverageCircle, Enrichment, FocusTrack, RouteEndpoints, WatchEntry } from '../types';
import { EMERGENCY_SQUAWKS, isParked, isWatched } from '../types';
import { altitudeBucket, altitudeColor, formatAltitude } from './altitude';
import { ICON_SPAN_PX, iconId, registerPlaneIcons } from './planeIcon';
import { airframeFor } from './airframes';
import { legCoordinates, type SmoothBudget } from './smooth';
import { circlePolygon, finalApproachFix, greatCirclePoints, offsetPoint } from './geo';
import { toFeatureCollection, type DensityGrid } from './density';

export const LANES_SOURCE = 'flight-lanes';
export const LANES_LAYER = 'flight-lanes-layer';
export const COVERAGE_SOURCE = 'coverage';
export const COVERAGE_LAYER = 'coverage-layer';
export const COVERAGE_LABELS_LAYER = 'coverage-labels-layer';
// User-drawn coverage circle (Layers menu "Coverage circle" tool) — see updateCoverageCircle.
export const COVERAGE_CIRCLE_SOURCE = 'coverage-circle';
export const COVERAGE_CIRCLE_FILL_LAYER = 'coverage-circle-fill-layer';
export const COVERAGE_CIRCLE_LINE_LAYER = 'coverage-circle-line-layer';
export const COVERAGE_CIRCLE_LABEL_LAYER = 'coverage-circle-label-layer';
export const PLANES_SOURCE = 'planes';
export const PLANES_LAYER = 'planes-layer';
export const PLANE_SELECTED_HALO_LAYER = 'plane-selected-halo-layer';
export const PLANE_EMERGENCY_HALO_LAYER = 'plane-emergency-halo-layer';
export const PLANE_WATCHED_HALO_LAYER = 'plane-watched-halo-layer';
export const TRAILS_SOURCE = 'trails';
export const TRAILS_LAYER = 'trails-layer';
export const FOCUS_TRACK_SOURCE = 'focus-track';
export const FOCUS_TRACK_LAYER = 'focus-track-layer';
export const FOCUS_TRACK_CASING_LAYER = 'focus-track-casing-layer';
export const STEMS_SOURCE = 'alt-stems';
export const STEMS_LAYER = 'alt-stems-layer';
export const STEM_GROUND_SOURCE = 'alt-stem-ground';
export const STEM_GROUND_LAYER = 'alt-stem-ground-layer';
export const AIRPORTS_SOURCE = 'airports';
export const AIRPORTS_LAYER = 'airports-layer';
export const AIRPORT_LABELS_LAYER = 'airport-labels-layer';
export const ROUTES_ALL_SOURCE = 'routes-all';
export const ROUTES_ALL_LAYER = 'routes-all-layer';
export const PREDICTED_SOURCE = 'predicted';
export const PREDICTED_LAYER = 'predicted-layer';
export const ROUTE_SOURCE = 'route';
export const ROUTE_FLOWN_LAYER = 'route-flown-layer';
export const ROUTE_REMAIN_LAYER = 'route-remain-layer';
export const ROUTE_POINTS_SOURCE = 'route-points';
export const ROUTE_POINTS_LAYER = 'route-points-layer';
export const ROUTE_POINT_LABELS_LAYER = 'route-point-labels-layer';
export const RINGS_SOURCE = 'rings';
export const RINGS_LAYER = 'rings-layer';
export const RING_LABELS_LAYER = 'ring-labels-layer';
// Hobbyist-mode approach overlays: centreline (localizer course), glide-slope
// ground projection, and a small circle marker at the FAF. Sources are always
// declared; visibility is toggled by `setApproachLayerVisible` so toggling
// hobbyist on/off is a single layout-property flip, not a style re-add.
export const APPROACH_LINE_SOURCE = 'approach-line';
export const APPROACH_LINE_LAYER = 'approach-line-layer';
export const GLIDE_SLOPE_SOURCE = 'glide-slope';
export const GLIDE_SLOPE_LAYER = 'glide-slope-layer';
// The actual 3D descent ramp (threshold to lifted FAF) shown in hobbyist 3D
// mode, separate from GLIDE_SLOPE_LAYER's flat past-threshold ground stub —
// see `updateGlideSlope3D`.
export const GLIDE_SLOPE_3D_SOURCE = 'glide-slope-3d';
export const GLIDE_SLOPE_3D_LAYER = 'glide-slope-3d-layer';
export const APPROACH_FAF_SOURCE = 'approach-faf';
export const APPROACH_FAF_LAYER = 'approach-faf-layer';

const KT_TO_MPS = 0.514444;
const NM_TO_FT = 6076.12;
const MAX_EXTRAPOLATION_MS = 30_000;
const TRAIL_MAX_AGE_MS = 15 * 60 * 1000;
const RING_RADII_KM = [50, 100, 150, 200];
/** Screen-space pixels an aircraft is lifted per foot of altitude in 3D mode, at full (90°) pitch. */
const PX_PER_FT_AT_FULL_PITCH = 0.012;
/** Duration of the smoothed transition between one dead-reckoned target and the next (see projectPosition). */
const INTERP_DURATION_MS = 400;

/** Shared pseudo-3D bead styling for circle markers (FAF, airports, route points) — a dark offset shadow beneath and a small light highlight above the flat circle, mirroring the plane icons' lit-from-above bevel. */
const MARKER_3D_SHADOW_COLOR = 'rgba(0, 0, 0, 0.4)';
const MARKER_3D_SHADOW_OFFSET: [number, number] = [1.3, 1.8];
const MARKER_3D_HIGHLIGHT_COLOR = 'rgba(255, 255, 255, 0.55)';
const MARKER_3D_HIGHLIGHT_OFFSET: [number, number] = [-1, -1.3];

/** Duration for paint-property fades on pure on/off overlay toggles (rings, coverage). Product-register: state change, not decoration. */
const TOGGLE_FADE_MS = 220;
const RINGS_OPACITY = 0.5;
const COVERAGE_OPACITY = 0.75;
const LABEL_OPACITY = 1;
/** Number of short segments an altitude stem is split into so opacity/width can fade smoothly from ground to the lifted aircraft position (MapLibre's `line-gradient` is a single ramp shared by every feature in the layer, so it can't vary per-feature by altitude color — per-segment properties do). */
const STEM_SEGMENTS = 5;
/** Selected-aircraft halo pulse period. */
const SELECTED_PULSE_PERIOD_MS = 1800;
const EMERGENCY_PULSE_PERIOD_MS = 900;

/** Per-aircraft interpolation state driving projectPosition's smoothing (see below). */
interface InterpState {
  lastSeen: number;
  from: [number, number];
  to: [number, number];
  start: number;
  end: number;
  /** Position actually rendered last frame — the ease must restart from here, not from the stale previous target, or the plane snaps backward on every data update. */
  displayed: [number, number];
}

const interpMap = new Map<string, InterpState>();

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export interface LayerOptions {
  projector: boolean;
  /**
   * Whether the map is currently in the "hobbyist" aviation view. Only used
   * for default visibility of the approach overlays — the layer-filter that
   * hides roads is applied separately via `applyHobbyistVisibility` once the
   * map's style has finished loading.
   */
  hobbyist: boolean;
}

const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });

/**
 * Tracks whether rings/coverage were visible on the previous call so
 * updateRings/updateCoverage can fade the on/off transition (see below)
 * instead of instant-popping via setData alone.
 */
let ringsOn = false;
let coverageOn = false;

/** Best-effort setPaintProperty — no-ops if the layer isn't ready yet (e.g. style still parsing). */
function setPaintOpacity(map: MlMap, layerId: string, prop: Parameters<MlMap['setPaintProperty']>[1], value: number): void {
  if (!map.getLayer(layerId)) return;
  try {
    map.setPaintProperty(layerId, prop, value);
  } catch {
    /* layer not yet ready */
  }
}

/**
 * True-to-scale aircraft. Each plane feature carries `k` (icon-size at zoom
 * 0 that makes its silhouette cover its real length/span on the ground at its
 * latitude) and `f` (a floor). Web Mercator halves the metres per pixel each
 * zoom level, so true size is k·2^zoom. Zoomed out, real size is sub-pixel
 * (a 737 is under 1 px at regional zoom), so icons stop shrinking at a floor
 * that still keeps their relative sizes (an A380 stays twice a 737).
 */
const EARTH_M_PER_PX_Z0 = 78271.517; // 512-px tiles: equator circumference / 512
/** Floor scale: 1.83 m per px ≈ true size at zoom 15 at mid latitudes (a 737 ≈ 22 px). */
const FLOOR_M_PER_PX = 1.83;
/** Absolute minimum on-screen size (px) for tiny aircraft at the floor. */
const MIN_ICON_PX = 9;
/** True-size mode: below real pixel size a plane is just a marker this big. */
const MARKER_PX = 7;

function sizeAt(zoom: number, floorScale: number): ExpressionSpecification {
  return ['max', ['*', ['get', 'f'], floorScale], ['*', ['get', 'k'], 2 ** zoom]];
}

/** icon-size: exact per integer zoom, exponential (base 2) in between — so exact everywhere above the floor. */
function iconSizeExpr(floorScale: number, mult = 1): ExpressionSpecification {
  const stops: (number | ExpressionSpecification)[] = [];
  for (let z = 0; z <= 24; z++) stops.push(z, mult === 1 ? sizeAt(z, floorScale) : ['*', sizeAt(z, floorScale), mult]);
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops] as ExpressionSpecification;
}

/** Halo radius (px) that hugs the plane at any size: ~60% of its on-screen span, never under `minPx`. */
function haloRadiusExpr(floorScale: number, minPx: number, mult = 1): ExpressionSpecification {
  const stops: (number | ExpressionSpecification)[] = [];
  for (let z = 0; z <= 24; z++) stops.push(z, ['max', minPx * mult, ['*', sizeAt(z, floorScale), ICON_SPAN_PX * 0.6 * mult]]);
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops] as ExpressionSpecification;
}

/** Projector mode's floor multiplier, remembered for the halo pulse in updatePlanes. */
let planeFloorScale = 1;

export function setupLayers(map: MlMap, opts: LayerOptions): void {
  registerPlaneIcons(map, opts.projector ? '#05050a' : '#12121e');
  const scale = opts.projector ? 1.6 : 1;
  planeFloorScale = scale;

  for (const src of [
    LANES_SOURCE,
    COVERAGE_SOURCE,
    COVERAGE_CIRCLE_SOURCE,
    RINGS_SOURCE,
    ROUTES_ALL_SOURCE,
    PREDICTED_SOURCE,
    TRAILS_SOURCE,
    FOCUS_TRACK_SOURCE,
    STEMS_SOURCE,
    STEM_GROUND_SOURCE,
    ROUTE_SOURCE,
    ROUTE_POINTS_SOURCE,
    AIRPORTS_SOURCE,
    APPROACH_LINE_SOURCE,
    GLIDE_SLOPE_SOURCE,
    GLIDE_SLOPE_3D_SOURCE,
    APPROACH_FAF_SOURCE,
  ]) {
    if (!map.getSource(src)) map.addSource(src, { type: 'geojson', data: empty() });
  }
  if (!map.getSource(PLANES_SOURCE)) {
    map.addSource(PLANES_SOURCE, { type: 'geojson', data: empty(), promoteId: 'icao24' });
  }

  // Observed flight-lane density (very bottom — an empirical traffic
  // heatmap, not official airway data; everything else draws on top of it).
  if (!map.getLayer(LANES_LAYER)) {
    map.addLayer({
      id: LANES_LAYER,
      type: 'heatmap',
      source: LANES_SOURCE,
      maxzoom: 12,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0, 20, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 2.5],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 4, 8, 10, 24],
        'heatmap-opacity': opts.projector ? 0.65 : 0.55,
        'heatmap-opacity-transition': { duration: TOGGLE_FADE_MS },
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(0,0,0,0)',
          0.2,
          'rgba(56,189,248,0.35)',
          0.4,
          'rgba(45,212,191,0.55)',
          0.6,
          'rgba(245,158,11,0.7)',
          0.8,
          'rgba(251,146,60,0.85)',
          1,
          'rgba(220,38,38,0.95)',
        ],
      },
    });
  }

  // Range rings.
  if (!map.getLayer(RINGS_LAYER)) {
    map.addLayer({
      id: RINGS_LAYER,
      type: 'line',
      source: RINGS_SOURCE,
      paint: {
        'line-color': opts.projector ? '#2b6cb0' : '#64748b',
        'line-opacity': RINGS_OPACITY,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': 1,
        'line-dasharray': [2, 3],
      },
    });
    map.addLayer({
      id: RING_LABELS_LAYER,
      type: 'symbol',
      source: RINGS_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        // 'line-center' places exactly one label at the line's midpoint —
        // 'line' placement repeats the label every `symbol-spacing` (default
        // ~250px), which tiles it across the whole ring on a large circle.
        'symbol-placement': 'line-center',
        'text-rotation-alignment': 'viewport',
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-offset': [0, -0.6],
      },
      paint: {
        'text-color': opts.projector ? '#7fb2e0' : '#475569',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
        'text-opacity': LABEL_OPACITY,
        'text-opacity-transition': { duration: TOGGLE_FADE_MS },
      },
    });
  }

  // Live data coverage boundary: the actual area currently queried for live
  // traffic (not physical receiver locations — those aren't published by the
  // free data sources this app uses).
  if (!map.getLayer(COVERAGE_LAYER)) {
    map.addLayer({
      id: COVERAGE_LAYER,
      type: 'line',
      source: COVERAGE_SOURCE,
      paint: {
        'line-color': opts.projector ? '#fbbf24' : '#f59e0b',
        'line-opacity': COVERAGE_OPACITY,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': 1.5,
        'line-dasharray': [6, 3],
      },
    });
    map.addLayer({
      id: COVERAGE_LABELS_LAYER,
      type: 'symbol',
      source: COVERAGE_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        // Bug fix: 'line' placement repeats the label every `symbol-spacing`
        // (default ~250px) along the whole line, which on a coverage
        // rectangle spanning most/all of the viewport tiled "Live data
        // coverage" repeatedly across the top/left/right edges. 'line-center'
        // places exactly one label at the line's midpoint, guaranteed.
        'symbol-placement': 'line-center',
        'text-rotation-alignment': 'viewport',
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
        'text-offset': [0, -0.6],
      },
      paint: {
        'text-color': opts.projector ? '#fcd34d' : '#b45309',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
        'text-opacity': LABEL_OPACITY,
        'text-opacity-transition': { duration: TOGGLE_FADE_MS },
      },
    });
  }

  // User-drawn coverage circle: same color family as the queried-bbox
  // rectangle above (both mean "this is the live data boundary"), but
  // filled + outlined + labelled since it's a user-placed area rather than
  // an outline-only honesty overlay.
  if (!map.getLayer(COVERAGE_CIRCLE_FILL_LAYER)) {
    map.addLayer({
      id: COVERAGE_CIRCLE_FILL_LAYER,
      type: 'fill',
      source: COVERAGE_CIRCLE_SOURCE,
      paint: {
        'fill-color': opts.projector ? '#fbbf24' : '#f59e0b',
        'fill-opacity': 0.08,
      },
    });
    map.addLayer({
      id: COVERAGE_CIRCLE_LINE_LAYER,
      type: 'line',
      source: COVERAGE_CIRCLE_SOURCE,
      paint: {
        'line-color': opts.projector ? '#fbbf24' : '#f59e0b',
        'line-opacity': COVERAGE_OPACITY,
        'line-width': 1.75,
      },
    });
    map.addLayer({
      id: COVERAGE_CIRCLE_LABEL_LAYER,
      type: 'symbol',
      source: COVERAGE_CIRCLE_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 11,
      },
      paint: {
        'text-color': opts.projector ? '#fcd34d' : '#b45309',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
      },
    });
  }

  // Full origin→destination route lines for every aircraft (dim, opt-in).
  if (!map.getLayer(ROUTES_ALL_LAYER)) {
    map.addLayer({
      id: ROUTES_ALL_LAYER,
      type: 'line',
      source: ROUTES_ALL_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': opts.projector ? '#38bdf8' : '#0ea5e9',
        'line-opacity': opts.projector ? 0.3 : 0.22,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': 1,
      },
    });
  }

  // Predicted remaining-leg line (current position -> known destination) for every aircraft.
  if (!map.getLayer(PREDICTED_LAYER)) {
    map.addLayer({
      id: PREDICTED_LAYER,
      type: 'line',
      source: PREDICTED_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': opts.projector ? '#fbbf24' : '#d97706',
        'line-opacity': opts.projector ? 0.6 : 0.5,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': opts.projector ? 2 : 1.3,
        'line-dasharray': [1.5, 2],
      },
    });
  }

  // Trails.
  if (!map.getLayer(TRAILS_LAYER)) {
    map.addLayer({
      id: TRAILS_LAYER,
      type: 'line',
      source: TRAILS_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-opacity': ['get', 'opacity'],
        // Subtle width taper toward the older (dimmer) end of the trail,
        // driven off the same per-segment `opacity` (freshness) property
        // already used for the color fade — reads as history trailing off
        // rather than a uniform-width line that merely dims.
        'line-width': [
          'interpolate',
          ['linear'],
          ['get', 'opacity'],
          0.15,
          opts.projector ? 1.6 : 1,
          0.9,
          opts.projector ? 3.5 : 2,
        ],
      },
    });
  }

  // Focused flight: the selected plane's whole recorded path (or a past
  // flight from its history), drawn bolder than trails with a dark casing so
  // it reads on top of everything else.
  if (!map.getLayer(FOCUS_TRACK_LAYER)) {
    map.addLayer({
      id: FOCUS_TRACK_CASING_LAYER,
      type: 'line',
      source: FOCUS_TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#0b0d10', 'line-opacity': 0.55, 'line-width': opts.projector ? 7 : 5 },
    });
    map.addLayer({
      id: FOCUS_TRACK_LAYER,
      type: 'line',
      source: FOCUS_TRACK_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': opts.projector ? 4 : 2.75 },
    });
  }

  // Altitude stems (3D mode): line from ground track up to the raised aircraft
  // position, plus a small ground-shadow dot, both colored/positioned by altitude.
  if (!map.getLayer(STEMS_LAYER)) {
    map.addLayer({
      id: STEM_GROUND_LAYER,
      type: 'circle',
      source: STEM_GROUND_SOURCE,
      paint: {
        'circle-radius': opts.projector ? 4.5 : 3.5,
        'circle-color': opts.projector ? '#000000' : '#334155',
        'circle-opacity': 0.5,
        // Soft blur so the ground-shadow dot reads as a diffuse contact
        // shadow rather than a hard-edged marker, reinforcing the "lifted"
        // 3D illusion of the stem above it.
        'circle-blur': 0.45,
        'circle-stroke-width': 1,
        'circle-stroke-color': opts.projector ? '#334155' : '#ffffff',
      },
    });
    map.addLayer({
      id: STEMS_LAYER,
      type: 'line',
      source: STEMS_SOURCE,
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        // Per-segment width/opacity (see STEM_SEGMENTS in updatePlanes) fade
        // the stem from faint near the ground to fully opaque at the
        // lifted aircraft, an opacity-falloff cue for the 3D lift. (MapLibre
        // `line-gradient` can't be used here since it's one ramp shared by
        // every feature in the layer, not per-feature by altitude color.)
        'line-width': ['*', ['get', 'width'], opts.projector ? 2.4 : 1.8],
        'line-opacity': ['get', 'opacity'],
        'line-dasharray': [1, 1.5],
      },
    });
  }

  // Selected-flight route: flown (solid) + remaining (dashed) + endpoint markers.
  if (!map.getLayer(ROUTE_FLOWN_LAYER)) {
    map.addLayer({
      id: ROUTE_FLOWN_LAYER,
      type: 'line',
      source: ROUTE_SOURCE,
      filter: ['==', ['get', 'kind'], 'flown'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#38bdf8',
        'line-width': opts.projector ? 3 : 2,
        'line-opacity': 0.9,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
      },
    });
    map.addLayer({
      id: ROUTE_REMAIN_LAYER,
      type: 'line',
      source: ROUTE_SOURCE,
      filter: ['==', ['get', 'kind'], 'remaining'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#38bdf8',
        'line-width': opts.projector ? 3 : 2,
        'line-opacity': 0.55,
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-dasharray': [1.5, 2],
      },
    });
    // Pseudo-3D bead: dark offset shadow beneath + small light highlight
    // above the flat marker, same lit-from-above bevel cue as the plane
    // icons (planeIcon.ts) and glide-slope ramp — see MARKER_3D_SHADOW/
    // MARKER_3D_HIGHLIGHT paint below.
    map.addLayer({
      id: `${ROUTE_POINTS_LAYER}-shadow`,
      type: 'circle',
      source: ROUTE_POINTS_SOURCE,
      paint: {
        'circle-radius': 5,
        'circle-color': MARKER_3D_SHADOW_COLOR,
        'circle-translate': MARKER_3D_SHADOW_OFFSET,
        'circle-blur': 0.3,
      },
    });
    map.addLayer({
      id: ROUTE_POINTS_LAYER,
      type: 'circle',
      source: ROUTE_POINTS_SOURCE,
      paint: {
        'circle-radius': 5,
        'circle-color': '#0ea5e9',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    map.addLayer({
      id: `${ROUTE_POINTS_LAYER}-highlight`,
      type: 'circle',
      source: ROUTE_POINTS_SOURCE,
      paint: { 'circle-radius': 2, 'circle-color': MARKER_3D_HIGHLIGHT_COLOR, 'circle-translate': MARKER_3D_HIGHLIGHT_OFFSET },
    });
    map.addLayer({
      id: ROUTE_POINT_LABELS_LAYER,
      type: 'symbol',
      source: ROUTE_POINTS_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Bold'],
        'text-size': opts.projector ? 15 : 12,
        'text-offset': [0, -1.2],
        'text-anchor': 'bottom',
      },
      paint: {
        'text-color': opts.projector ? '#e0f2fe' : '#0369a1',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.4,
      },
    });
  }

  // Approach overlays for the aviation "hobbyist" view. Always registered so
  // toggling hobbyist is purely a visibility flip, but start hidden so the
  // map isn't noisy by default. Now that the data can include every runway
  // end worldwide (not just ~15 curated US airports), also minzoom-gate them
  // — same idea as AIRPORT_LABELS_LAYER below — so a zoomed-out view over a
  // dense region doesn't get flooded with hundreds of small-strip approach
  // lines; the source data itself is also bbox/limit-capped server-side.
  const APPROACH_MINZOOM = opts.projector ? 6 : 8;
  if (!map.getLayer(APPROACH_LINE_LAYER)) {
    map.addLayer({
      id: APPROACH_LINE_LAYER,
      type: 'line',
      source: APPROACH_LINE_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: {
        'line-cap': 'round',
        // visibility: 'none' until hobbyist is on; flipped by the view layer.
        visibility: opts.hobbyist ? 'visible' : 'none',
      },
      paint: {
        'line-color': opts.projector ? '#bae6fd' : '#7dd3fc',
        // Prominent (large-airport) approaches read as the "common approach"
        // for that hub — drawn brighter/thicker than a small-strip approximation.
        'line-opacity': ['case', ['get', 'prominent'], 1, 0.75],
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': ['case', ['get', 'prominent'], opts.projector ? 3.2 : 2.4, opts.projector ? 2 : 1.4],
        'line-dasharray': [4, 2],
      },
    });
  }
  if (!map.getLayer(GLIDE_SLOPE_LAYER)) {
    map.addLayer({
      id: GLIDE_SLOPE_LAYER,
      type: 'line',
      source: GLIDE_SLOPE_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: {
        'line-cap': 'round',
        visibility: opts.hobbyist ? 'visible' : 'none',
      },
      paint: {
        'line-color': opts.projector ? '#f5d0fe' : '#f0abfc',
        'line-opacity': ['case', ['get', 'prominent'], 1, 0.7],
        'line-opacity-transition': { duration: TOGGLE_FADE_MS },
        'line-width': ['case', ['get', 'prominent'], opts.projector ? 2.6 : 1.9, opts.projector ? 1.6 : 1.1],
        'line-dasharray': [2, 2],
      },
    });
  }
  // 3D descent ramp: threshold to lifted-FAF, updated every tick from the
  // live camera pitch (see `updateGlideSlope3D`) rather than once here —
  // starts empty/hidden like the others until hobbyist+3D actually raise it.
  if (!map.getLayer(GLIDE_SLOPE_3D_LAYER)) {
    map.addLayer({
      id: GLIDE_SLOPE_3D_LAYER,
      type: 'line',
      source: GLIDE_SLOPE_3D_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: {
        'line-cap': 'round',
        visibility: opts.hobbyist ? 'visible' : 'none',
      },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['*', ['get', 'width'], opts.projector ? 2.2 : 1.6],
        'line-opacity': ['get', 'opacity'],
      },
    });
  }
  if (!map.getLayer(APPROACH_FAF_LAYER)) {
    const fafVisibility: 'visible' | 'none' = opts.hobbyist ? 'visible' : 'none';
    map.addLayer({
      id: `${APPROACH_FAF_LAYER}-shadow`,
      type: 'circle',
      source: APPROACH_FAF_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: { visibility: fafVisibility },
      paint: {
        'circle-radius': ['case', ['get', 'prominent'], 6, 4],
        'circle-color': MARKER_3D_SHADOW_COLOR,
        'circle-translate': MARKER_3D_SHADOW_OFFSET,
        'circle-blur': 0.3,
      },
    });
    map.addLayer({
      id: APPROACH_FAF_LAYER,
      type: 'circle',
      source: APPROACH_FAF_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: { visibility: fafVisibility },
      paint: {
        'circle-radius': ['case', ['get', 'prominent'], 6, 4],
        'circle-color': opts.projector ? '#0c2a3a' : '#7dd3fc',
        'circle-opacity-transition': { duration: TOGGLE_FADE_MS },
        'circle-stroke-width': ['case', ['get', 'prominent'], 2, 1.3],
        'circle-stroke-color': opts.projector ? '#bae6fd' : '#0c2a3a',
      },
    });
    map.addLayer({
      id: `${APPROACH_FAF_LAYER}-highlight`,
      type: 'circle',
      source: APPROACH_FAF_SOURCE,
      minzoom: APPROACH_MINZOOM,
      layout: { visibility: fafVisibility },
      paint: {
        'circle-radius': ['case', ['get', 'prominent'], 2.4, 1.6],
        'circle-color': MARKER_3D_HIGHLIGHT_COLOR,
        'circle-translate': MARKER_3D_HIGHLIGHT_OFFSET,
      },
    });
  }

  // Airports.
  if (!map.getLayer(AIRPORTS_LAYER)) {
    map.addLayer({
      id: `${AIRPORTS_LAYER}-shadow`,
      type: 'circle',
      source: AIRPORTS_SOURCE,
      paint: {
        'circle-radius': ['match', ['get', 'type'], 'large_airport', 7, 'medium_airport', 5, 3],
        'circle-color': MARKER_3D_SHADOW_COLOR,
        'circle-translate': MARKER_3D_SHADOW_OFFSET,
        'circle-blur': 0.3,
      },
    });
    map.addLayer({
      id: AIRPORTS_LAYER,
      type: 'circle',
      source: AIRPORTS_SOURCE,
      paint: {
        'circle-radius': ['match', ['get', 'type'], 'large_airport', 7, 'medium_airport', 5, 3],
        'circle-color': opts.projector ? '#00e5ff' : '#0e7490',
        'circle-opacity': 0.85,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': opts.projector ? '#003a44' : '#ffffff',
      },
    });
    map.addLayer({
      id: `${AIRPORTS_LAYER}-highlight`,
      type: 'circle',
      source: AIRPORTS_SOURCE,
      paint: {
        'circle-radius': ['match', ['get', 'type'], 'large_airport', 2.8, 'medium_airport', 2, 1.2],
        'circle-color': MARKER_3D_HIGHLIGHT_COLOR,
        'circle-translate': MARKER_3D_HIGHLIGHT_OFFSET,
      },
    });
    map.addLayer({
      id: AIRPORT_LABELS_LAYER,
      type: 'symbol',
      source: AIRPORTS_SOURCE,
      minzoom: opts.projector ? 6 : 7,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': (opts.projector ? 14 : 11) as number,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': opts.projector ? '#7fdbea' : '#155e70',
        'text-halo-color': opts.projector ? '#000000' : '#ffffff',
        'text-halo-width': 1.2,
      },
      filter: ['!=', ['get', 'type'], 'small_airport'],
    });
  }

  // Watched-aircraft halo: a steady (non-pulsing) subtle cyan glow under any
  // aircraft on the watchlist, driven off the `watched` feature property set
  // in updatePlanes. Added before the selected/emergency halos so those draw
  // on top when they overlap on the same aircraft.
  if (!map.getLayer(PLANE_WATCHED_HALO_LAYER)) {
    map.addLayer({
      id: PLANE_WATCHED_HALO_LAYER,
      type: 'circle',
      source: PLANES_SOURCE,
      filter: ['==', ['get', 'watched'], true],
      paint: {
        'circle-color': '#22d3ee',
        'circle-blur': 0.75,
        'circle-radius': haloRadiusExpr(scale, 9),
        'circle-opacity': 0.4,
        'circle-radius-transition': { duration: 200 },
        'circle-opacity-transition': { duration: 200 },
      },
    });
  }

  // Selected-aircraft halo: a soft pulsing glow underneath the selected
  // plane's icon, driven every ~150ms from updatePlanes() (piggybacking on
  // its existing tick cadence rather than running a separate rAF loop that
  // would need its own teardown). Shares PLANES_SOURCE and its `selected`
  // boolean/position, so it always tracks the icon with zero extra bookkeeping.
  if (!map.getLayer(PLANE_SELECTED_HALO_LAYER)) {
    map.addLayer({
      id: PLANE_SELECTED_HALO_LAYER,
      type: 'circle',
      source: PLANES_SOURCE,
      filter: ['==', ['get', 'selected'], true],
      paint: {
        'circle-color': '#ffb020',
        'circle-blur': 0.75,
        'circle-radius': haloRadiusExpr(scale, 9),
        'circle-opacity': 0,
        'circle-radius-transition': { duration: 200 },
        'circle-opacity-transition': { duration: 200 },
      },
    });
  }

  // Emergency-squawk halo: pulsing red glow under any aircraft squawking
  // 7500/7600/7700, regardless of selection. Same piggyback-on-updatePlanes
  // tick pattern as PLANE_SELECTED_HALO_LAYER above.
  if (!map.getLayer(PLANE_EMERGENCY_HALO_LAYER)) {
    map.addLayer({
      id: PLANE_EMERGENCY_HALO_LAYER,
      type: 'circle',
      source: PLANES_SOURCE,
      filter: ['==', ['get', 'emergency'], true],
      paint: {
        'circle-color': '#ff2d2d',
        'circle-blur': 0.75,
        'circle-radius': haloRadiusExpr(scale, 11),
        'circle-opacity': 0.5,
        'circle-radius-transition': { duration: 200 },
        'circle-opacity-transition': { duration: 200 },
      },
    });
  }

  // Planes (top) + labels.
  if (!map.getLayer(PLANES_LAYER)) {
    map.addLayer({
      id: PLANES_LAYER,
      type: 'symbol',
      source: PLANES_SOURCE,
      layout: {
        'icon-image': [
          'case',
          ['get', 'emergency'], ['get', 'iconEmergency'],
          ['get', 'selected'], ['get', 'iconSelected'],
          ['get', 'icon'],
        ],
        'icon-size': iconSizeExpr(scale),
        'icon-rotate': ['get', 'track'],
        'icon-rotation-alignment': 'map',
        'icon-pitch-alignment': 'viewport',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      // Parked aircraft (on the ground, transponder quiet) are drawn dimmed at their last spot.
      paint: { 'icon-opacity': ['case', ['get', 'selected'], 1, ['get', 'parked'], 0.5, 1] },
    });
  }
}

/** Dead-reckoned target: project the last fix along track at ground speed. */
function projectTarget(ac: Aircraft, now: number): [number, number] {
  const dtMs = Math.min(Math.max(now - ac.lastSeen, 0), MAX_EXTRAPOLATION_MS);
  if (ac.onGround || ac.groundSpeedKt == null || ac.track == null || dtMs <= 0) {
    return [ac.lon, ac.lat];
  }
  const distM = ac.groundSpeedKt * KT_TO_MPS * (dtMs / 1000);
  const rad = (ac.track * Math.PI) / 180;
  const dLat = (distM * Math.cos(rad)) / 111_320;
  const dLon = (distM * Math.sin(rad)) / (111_320 * Math.cos((ac.lat * Math.PI) / 180));
  return [ac.lon + dLon, ac.lat + dLat];
}

/**
 * Smoothly interpolated display position: eases between the previously
 * displayed position and the latest dead-reckoned target over
 * INTERP_DURATION_MS, so aircraft glide between 2 s server updates instead
 * of jumping. Restarts the interpolation (from wherever was last displayed)
 * whenever `ac.lastSeen` advances, i.e. new data arrived.
 *
 * The easing is done in *screen* (pixel) space, not lng/lat: a straight line
 * in geographic coordinates renders as a curved arc under MapLibre's web
 * mercator projection, so lerping raw lat/lon made aircraft bow visibly
 * off-course between fixes (the "rubber band"). Projecting both endpoints to
 * pixels, lerping there, and unprojecting back yields an on-screen straight
 * line that tracks the ground track.
 */
export function projectPosition(ac: Aircraft, now: number, map: MlMap): [number, number] {
  const target = projectTarget(ac, now);
  const state = interpMap.get(ac.icao24);
  if (!state) {
    interpMap.set(ac.icao24, { lastSeen: ac.lastSeen, from: target, to: target, start: now, end: now, displayed: target });
    return target;
  }
  if (state.lastSeen !== ac.lastSeen) {
    state.from = state.displayed;
    // Ease toward where the aircraft will be when the ease finishes, so the
    // handoff back to pure dead reckoning at state.end is seamless.
    state.to = projectTarget(ac, now + INTERP_DURATION_MS);
    state.lastSeen = ac.lastSeen;
    state.start = now;
    state.end = now + INTERP_DURATION_MS;
  }
  if (now >= state.end) {
    state.displayed = target;
    return target;
  }
  const t = (now - state.start) / (state.end - state.start);
  const eased = t * (2 - t); // ease-out
  const fromPx = map.project({ lng: state.from[0], lat: state.from[1] });
  const toPx = map.project({ lng: state.to[0], lat: state.to[1] });
  const lngLat = map.unproject([lerp(fromPx.x, toPx.x, eased), lerp(fromPx.y, toPx.y, eased)]);
  state.displayed = [lngLat.lng, lngLat.lat];
  return state.displayed;
}

/**
 * In 3D mode, lift a ground point in screen space by an amount proportional
 * to altitude and current map pitch (0 pitch = flat, no lift), then
 * unproject back to lng/lat. This is a stylized (not to-scale) elevation cue
 * — MapLibre's 2D symbol layers have no true per-feature height — but it
 * reads correctly under pan/zoom/rotate since it's recomputed every frame
 * from the live camera transform.
 */
function raise(map: MlMap, ground: [number, number], altitudeFt: number | null, onGround: boolean, threeD: boolean): [number, number] {
  if (!threeD || onGround || altitudeFt == null || altitudeFt <= 0) return ground;
  const pitch = map.getPitch();
  if (pitch <= 0) return ground;
  const p = map.project(ground);
  const offsetPx = altitudeFt * PX_PER_FT_AT_FULL_PITCH * (pitch / 90);
  const ll = map.unproject([p.x, p.y - offsetPx]);
  return [ll.lng, ll.lat];
}

export function updatePlanes(
  map: MlMap,
  aircraft: Aircraft[],
  selected: string | null,
  now: number,
  threeD: boolean,
  watchlist: WatchEntry[] = [],
  trueScale = true,
): void {
  const src = map.getSource(PLANES_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const stemSrc = map.getSource(STEMS_SOURCE) as GeoJSONSource | undefined;
  const groundSrc = map.getSource(STEM_GROUND_SOURCE) as GeoJSONSource | undefined;

  // Prune interpolation state for aircraft that are no longer visible so the
  // map doesn't retain memory for every aircraft that has ever appeared.
  const visibleIds = new Set(aircraft.map((ac) => ac.icao24));
  for (const icao of interpMap.keys()) {
    if (!visibleIds.has(icao)) interpMap.delete(icao);
  }

  const features: GeoJSON.Feature[] = [];
  const stems: GeoJSON.Feature[] = [];
  const grounds: GeoJSON.Feature[] = [];

  for (const ac of aircraft) {
    const frame = airframeFor(ac.typeCode, ac.category);
    const realM = Math.max(frame.length, frame.span);
    const cosLat = Math.max(Math.cos((ac.lat * Math.PI) / 180), 0.05);
    const bucket = altitudeBucket(ac.altitudeFt, ac.onGround);
    const ground = projectPosition(ac, now, map);
    const raised = raise(map, ground, ac.altitudeFt, ac.onGround, threeD);
    const emergency = ac.squawk != null && EMERGENCY_SQUAWKS.has(ac.squawk);

    features.push({
      type: 'Feature',
      id: ac.icao24,
      geometry: { type: 'Point', coordinates: raised },
      properties: {
        icao24: ac.icao24,
        icon: iconId(frame.key, bucket),
        parked: isParked(ac, now),
        iconSelected: iconId(frame.key, 'sel'),
        iconEmergency: iconId(frame.key, 'emg'),
        // See iconSizeExpr: true scale at zoom 0, and the zoomed-out floor.
        k: realM / (EARTH_M_PER_PX_Z0 * cosLat * ICON_SPAN_PX),
        f: (trueScale ? MARKER_PX : Math.max(realM / FLOOR_M_PER_PX, MIN_ICON_PX)) / ICON_SPAN_PX,
        track: ac.track ?? 0,
        selected: ac.icao24 === selected,
        emergency,
        watched: isWatched(ac, watchlist),
        label: emergency
          ? `${ac.callsign ?? ac.icao24.toUpperCase()}\nSQUAWK ${ac.squawk}\n${formatAltitude(ac.altitudeFt, ac.onGround)}`
          : `${ac.callsign ?? ac.icao24.toUpperCase()}\n${formatAltitude(ac.altitudeFt, ac.onGround)}`,
      },
    });

    if (raised[0] !== ground[0] || raised[1] !== ground[1]) {
      const color = altitudeColor(ac.altitudeFt, false);
      // Split into short segments, each a little more opaque/wider than the
      // last, so the stem visually fades in from the ground up to the
      // lifted aircraft rather than being a single flat-opacity line.
      for (let i = 0; i < STEM_SEGMENTS; i++) {
        const t0 = i / STEM_SEGMENTS;
        const t1 = (i + 1) / STEM_SEGMENTS;
        const tMid = (t0 + t1) / 2;
        stems.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [ground[0] + (raised[0] - ground[0]) * t0, ground[1] + (raised[1] - ground[1]) * t0],
              [ground[0] + (raised[0] - ground[0]) * t1, ground[1] + (raised[1] - ground[1]) * t1],
            ],
          },
          properties: { color, opacity: 0.12 + 0.68 * tMid, width: 0.55 + 0.85 * tMid },
        });
      }
      grounds.push({ type: 'Feature', geometry: { type: 'Point', coordinates: ground }, properties: {} });
    }
  }

  src.setData({ type: 'FeatureCollection', features });
  if (stemSrc) stemSrc.setData({ type: 'FeatureCollection', features: stems });
  if (groundSrc) groundSrc.setData({ type: 'FeatureCollection', features: grounds });

  // Pulse the selected-aircraft halo. Piggybacks on this function's existing
  // ~150ms tick cadence (see MapView's tick loop) rather than a separate rAF
  // loop, so there's nothing extra to start/stop on mount/unmount.
  if (map.getLayer(PLANE_SELECTED_HALO_LAYER)) {
    if (selected) {
      const phase = ((now % SELECTED_PULSE_PERIOD_MS) / SELECTED_PULSE_PERIOD_MS) * Math.PI * 2;
      const p = (Math.sin(phase) + 1) / 2; // 0..1 breathing value
      map.setPaintProperty(PLANE_SELECTED_HALO_LAYER, 'circle-opacity', 0.3 + 0.35 * p);
      map.setPaintProperty(PLANE_SELECTED_HALO_LAYER, 'circle-radius', haloRadiusExpr(planeFloorScale, 9, 1 + 0.25 * p));
    } else {
      map.setPaintProperty(PLANE_SELECTED_HALO_LAYER, 'circle-opacity', 0);
    }
  }

  // Pulse the emergency-squawk halo (faster/sharper than the selected pulse
  // so it reads as urgent). Runs unconditionally — the layer's filter alone
  // decides which features (if any) it's drawn under.
  if (map.getLayer(PLANE_EMERGENCY_HALO_LAYER)) {
    const phase = ((now % EMERGENCY_PULSE_PERIOD_MS) / EMERGENCY_PULSE_PERIOD_MS) * Math.PI * 2;
    const p = (Math.sin(phase) + 1) / 2;
    map.setPaintProperty(PLANE_EMERGENCY_HALO_LAYER, 'circle-opacity', 0.35 + 0.5 * p);
  }
}

/**
 * Trails as per-pair segments so color (altitude) and opacity (age) vary
 * along the line. `maxAgeMs` bounds which segments are drawn at all (pass
 * Infinity for "full trail" mode); the age->opacity fade always uses the
 * same 15-min window so recent history stays bright and older history
 * settles at a dim floor rather than growing uniformly duller forever.
 */
/**
 * `aircraft` is the currently-visible list, drawn at full opacity.
 * `fading` (optional) is aircraft that just dropped out of view (landed and
 * filtered out, or genuinely lost) — each carries its own 0-1 `fadeOpacity`
 * multiplier so the caller can animate the trail shrinking away over a
 * short window instead of it vanishing instantly on the next redraw.
 */
export function updateTrails(
  map: MlMap,
  aircraft: Aircraft[],
  now: number,
  maxAgeMs: number = TRAIL_MAX_AGE_MS,
  fading: { ac: Aircraft; fadeOpacity: number }[] = [],
): void {
  const src = map.getSource(TRAILS_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = [];
  // Curves between fixes (see smooth.ts) only where the bend is visible, and
  // within a vertex budget so a crowded map never turns into a slow redraw.
  const budget: SmoothBudget = { left: map.getZoom() >= SMOOTH_MIN_ZOOM ? TRAIL_SMOOTH_BUDGET : 0 };
  const addTrail = (ac: Aircraft, fadeOpacity: number) => {
    const t = ac.trail;
    for (let i = 1; i < t.length; i++) {
      const a = t[i - 1];
      const b = t[i];
      const age = now - b.ts;
      if (age > maxAgeMs) continue;
      const freshness = Math.max(0, 1 - age / TRAIL_MAX_AGE_MS);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: legCoordinates(a, b, budget) },
        properties: {
          color: altitudeColor(b.altitudeFt, false),
          opacity: (0.15 + 0.75 * freshness) * fadeOpacity,
        },
      });
    }
    const last = t[t.length - 1];
    if (last) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [[last.lon, last.lat], [ac.lon, ac.lat]] },
        properties: { color: altitudeColor(ac.altitudeFt, ac.onGround), opacity: 0.9 * fadeOpacity },
      });
    }
  };
  for (const ac of aircraft) addTrail(ac, 1);
  for (const { ac, fadeOpacity } of fading) addTrail(ac, fadeOpacity);
  src.setData({ type: 'FeatureCollection', features });
}

/** Trails get curved between fixes from this zoom up (below it the bends are sub-pixel), within this many vertices. */
const SMOOTH_MIN_ZOOM = 6;
const TRAIL_SMOOTH_BUDGET = 40_000;
const FOCUS_SMOOTH_BUDGET = 60_000;

/**
 * Draw a focused flight's full path as altitude-colored segments. For a live
 * flight, `tail` (the plane's current position) closes the gap between the
 * last recorded point and the icon.
 */
export function updateFocusTrack(map: MlMap, focus: FocusTrack | null, tail?: Aircraft): void {
  const src = map.getSource(FOCUS_TRACK_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = [];
  const pts = focus?.points ?? [];
  // The focused flight is the one being studied: always curve it.
  const budget: SmoothBudget = { left: FOCUS_SMOOTH_BUDGET };
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    // Long silences (coverage gaps) are left open instead of drawn as a
    // misleading straight line across them.
    if (b.ts - a.ts > 20 * 60_000) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: legCoordinates(a, b, budget) },
      properties: { color: altitudeColor(b.altitudeFt, b.onGround) },
    });
  }
  const last = pts[pts.length - 1];
  if (focus?.live && tail && last) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[last.lon, last.lat], [tail.lon, tail.lat]] },
      properties: { color: altitudeColor(tail.altitudeFt, tail.onGround) },
    });
  }
  src.setData({ type: 'FeatureCollection', features });
}

export function updateAirports(map: MlMap, airports: Airport[]): void {
  const src = map.getSource(AIRPORTS_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = airports.map((a) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
    properties: {
      type: a.type,
      // Carried so a click on the marker can open that airport's board.
      ident: a.ident,
      label: a.iata ? `${a.iata} · ${a.name}` : a.name,
    },
  }));
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Draw the selected flight's route: origin→current (flown, solid) and
 * current→destination (remaining, dashed), with labelled endpoint markers.
 * Pass a null enrichment (or one without coordinates) to clear it.
 */
export function updateRoute(
  map: MlMap,
  aircraft: Aircraft | undefined,
  enrichment: Enrichment | null,
  now: number,
): void {
  const lineSrc = map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
  const ptSrc = map.getSource(ROUTE_POINTS_SOURCE) as GeoJSONSource | undefined;
  if (!lineSrc || !ptSrc) return;

  const route = enrichment?.route;
  const features: GeoJSON.Feature[] = [];
  const points: GeoJSON.Feature[] = [];

  if (aircraft && route) {
    const pos = projectPosition(aircraft, now, map);
    if (route.originLat != null && route.originLon != null) {
      const origin: [number, number] = [route.originLon, route.originLat];
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: greatCirclePoints(origin, pos) },
        properties: { kind: 'flown' },
      });
      points.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: origin },
        properties: { label: route.originIcao ?? 'ORIG' },
      });
    }
    if (route.destinationLat != null && route.destinationLon != null) {
      const dest: [number, number] = [route.destinationLon, route.destinationLat];
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: greatCirclePoints(pos, dest) },
        properties: { kind: 'remaining' },
      });
      points.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: dest },
        properties: { label: route.destinationIcao ?? 'DEST' },
      });
    }
  }

  lineSrc.setData({ type: 'FeatureCollection', features });
  ptSrc.setData({ type: 'FeatureCollection', features: points });
}

/**
 * Full origin→destination great-circle line for every aircraft with known
 * route endpoints. Pass an empty object to clear the layer.
 */
export function updateAllRoutes(map: MlMap, routes: Record<string, RouteEndpoints>): void {
  const src = map.getSource(ROUTES_ALL_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = [];
  for (const icao of Object.keys(routes)) {
    const r = routes[icao];
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: greatCirclePoints([r.originLon, r.originLat], [r.destinationLon, r.destinationLat]),
      },
      properties: {},
    });
  }
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Predicted remaining-leg line for every aircraft with a known destination:
 * a dashed great-circle line from its live (dead-reckoned) position to that
 * destination, redrawn every tick as the aircraft moves. Pass an empty
 * aircraft list or routes map to clear it.
 */
export function updatePredicted(
  map: MlMap,
  aircraft: Aircraft[],
  routes: Record<string, RouteEndpoints>,
  now: number,
): void {
  const src = map.getSource(PREDICTED_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = [];
  for (const ac of aircraft) {
    const r = routes[ac.icao24];
    if (!r) continue;
    const pos = projectPosition(ac, now, map);
    const dest: [number, number] = [r.destinationLon, r.destinationLat];
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: greatCirclePoints(pos, dest) },
      properties: {},
    });
  }
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Concentric range rings around a center, labelled in km. Pass null to
 * clear/hide. On/off is a smooth opacity fade rather than an instant pop:
 * turning on draws the new geometry at zero opacity then fades it in on the
 * next frame (a paint-property change on already-drawn features animates via
 * the `line-opacity-transition`/`text-opacity-transition` set at layer
 * creation, whereas `setData` itself can't be animated); turning off fades
 * to zero opacity and leaves the last geometry in the source (harmless while
 * invisible) so a later re-enable has something to fade from instead of
 * popping back in.
 */
export function updateRings(map: MlMap, center: [number, number] | null): void {
  const src = map.getSource(RINGS_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const on = center != null;

  if (on) {
    const features: GeoJSON.Feature[] = RING_RADII_KM.map((km) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: circlePolygon(center as [number, number], km) },
      properties: { label: `${km} km` },
    }));
    if (!ringsOn) {
      setPaintOpacity(map, RINGS_LAYER, 'line-opacity', 0);
      setPaintOpacity(map, RING_LABELS_LAYER, 'text-opacity', 0);
      src.setData({ type: 'FeatureCollection', features });
      requestAnimationFrame(() => {
        setPaintOpacity(map, RINGS_LAYER, 'line-opacity', RINGS_OPACITY);
        setPaintOpacity(map, RING_LABELS_LAYER, 'text-opacity', LABEL_OPACITY);
      });
    } else {
      src.setData({ type: 'FeatureCollection', features });
    }
  } else if (ringsOn) {
    setPaintOpacity(map, RINGS_LAYER, 'line-opacity', 0);
    setPaintOpacity(map, RING_LABELS_LAYER, 'text-opacity', 0);
  }
  ringsOn = on;
}

/**
 * Draw the live data coverage boundary: the literal bbox currently being
 * requested from the server (and thus from each upstream ADS-B source) for
 * this session. This is NOT a radar/receiver range — the free sources this
 * app uses (OpenSky, adsb.fi, adsb.lol) don't publish individual
 * ground-receiver locations — it's simply the real query region, so users
 * know what area's data they're actually seeing right now. Pass null to
 * clear/hide. Uses the same fade-in/fade-out pattern as updateRings (see its
 * doc comment) rather than an instant setData pop.
 */
export function updateCoverage(map: MlMap, bbox: Bbox | null): void {
  const src = map.getSource(COVERAGE_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  const on = bbox != null;

  if (on) {
    const b = bbox as Bbox;
    const ring: [number, number][] = [
      [b.lomin, b.lamin],
      [b.lomax, b.lamin],
      [b.lomax, b.lamax],
      [b.lomin, b.lamax],
      [b.lomin, b.lamin],
    ];
    const features: GeoJSON.Feature[] = [
      { type: 'Feature', geometry: { type: 'LineString', coordinates: ring }, properties: { label: 'Live data coverage' } },
    ];
    if (!coverageOn) {
      setPaintOpacity(map, COVERAGE_LAYER, 'line-opacity', 0);
      setPaintOpacity(map, COVERAGE_LABELS_LAYER, 'text-opacity', 0);
      src.setData({ type: 'FeatureCollection', features });
      requestAnimationFrame(() => {
        setPaintOpacity(map, COVERAGE_LAYER, 'line-opacity', COVERAGE_OPACITY);
        setPaintOpacity(map, COVERAGE_LABELS_LAYER, 'text-opacity', LABEL_OPACITY);
      });
    } else {
      src.setData({ type: 'FeatureCollection', features });
    }
  } else if (coverageOn) {
    setPaintOpacity(map, COVERAGE_LAYER, 'line-opacity', 0);
    setPaintOpacity(map, COVERAGE_LABELS_LAYER, 'text-opacity', 0);
  }
  coverageOn = on;
}

/**
 * Draw the user-drawn coverage circle (Layers menu "Coverage circle" tool):
 * a filled circle + outline + radius label, pinning live coverage to a
 * user-placed area instead of the queried-bbox rectangle `updateCoverage`
 * draws for follow-view. Called on every mousemove while the draw tool is
 * armed (cheap setData, same pattern as the other overlays), so unlike
 * updateRings/updateCoverage above this skips the fade-in/out — it would
 * fight the constant redraws during a live drag. Pass null to clear.
 */
export function updateCoverageCircle(map: MlMap, circle: CoverageCircle | null): void {
  const src = map.getSource(COVERAGE_CIRCLE_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  if (!circle) {
    src.setData(empty());
    return;
  }
  const ring = circlePolygon([circle.lon, circle.lat], circle.radiusKm, 64);
  const nm = Math.round(circle.radiusKm * 0.539957);
  const label = `${Math.round(circle.radiusKm)} km · ${nm} nm`;
  src.setData({
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { label } }],
  });
}

/**
 * Render the observed flight-lane density grid as heatmap points. Pass
 * `null` (e.g. when the overlay is toggled off) to clear the layer.
 */
export function updateFlightLanes(map: MlMap, grid: DensityGrid | null): void {
  const src = map.getSource(LANES_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  src.setData(grid ? toFeatureCollection(grid) : empty());
}

/**
 * Layer IDs in the OpenFreeMap tile styles that obscure a clean aviation
 * view. Hidden as a single sweep when hobbyist mode is on, restored when
 * it's off. Aeroway layers (taxiways, runways) and waterway+water labels
 * are intentionally NOT in this list — they're aviation- or
 * navigation-relevant visual context.
 */
const HIDE_ON_HOBBYIST: ReadonlySet<string> = new Set([
  'highway_path',
  'highway_minor',
  'highway_major_casing',
  'highway_major_inner',
  'highway_major_subtle',
  'highway_motorway_casing',
  'highway_motorway_inner',
  'highway_motorway_subtle',
  'road_oneway',
  'road_oneway_opposite',
  'highway_name_other',
  'highway_name_motorway',
  'road_area_pier',
  'road_pier',
  'place_other',
  'place_suburb',
  'place_village',
  'place_town',
  'place_city',
  'building',
  'railway',
  'railway_transit',
  'railway_minor',
  'railway_dashline',
]);

/**
 * Push the layer-visibility filter that strips streets/buildings/POIs from
 * the underlying OpenFreeMap style when the aviation hobbyist toggle is on.
 * Uses runtime `visibility: none|visible` on the layout property — no
 * style re-spec / re-parse needed. Tries every layer in the list and
 * silently no-ops on ones not present in the current style.
 */
export function applyHobbyistVisibility(map: MlMap, hobbyist: boolean): void {
  const layers = map.getStyle()?.layers ?? [];
  for (const layer of layers) {
    if (!layer.id || !HIDE_ON_HOBBYIST.has(layer.id)) continue;
    if (!map.getLayer(layer.id)) continue;
    try {
      map.setLayoutProperty(layer.id, 'visibility', hobbyist ? 'none' : 'visible');
    } catch {
      /* layer not yet ready (e.g. styledata fired before parse complete) */
    }
  }
}

/**
 * Push the approach-procedure overlays into the map sources. Each entry
 * produces three features: a centreline line from the computed FAF back-az
 * to the runway threshold, a short dashed ground projection of the glide-
 * slope past touchdown, and a circle marker at the FAF.
 */
export function updateApproaches(map: MlMap, approaches: ApproachProcedure[]): void {
  const lineSrc = map.getSource(APPROACH_LINE_SOURCE) as GeoJSONSource | undefined;
  const glideSrc = map.getSource(GLIDE_SLOPE_SOURCE) as GeoJSONSource | undefined;
  const fafSrc = map.getSource(APPROACH_FAF_SOURCE) as GeoJSONSource | undefined;
  if (!lineSrc || !glideSrc || !fafSrc) return;

  const lineFeatures: GeoJSON.Feature[] = [];
  const glideFeatures: GeoJSON.Feature[] = [];
  const fafFeatures: GeoJSON.Feature[] = [];

  for (const a of approaches) {
    const approachNm = a.approachNm ?? 7;
    const gsLead = a.glideSlopeLeadNm ?? 1.5;
    const threshold: [number, number] = [a.thresholdLon, a.thresholdLat];
    const faf = finalApproachFix(a.thresholdLat, a.thresholdLon, a.courseDeg, approachNm);
    const gsEnd = offsetPoint(a.thresholdLat, a.thresholdLon, a.courseDeg, gsLead);

    const props = {
      airportIcao: a.airportIcao,
      runway: a.runwayIdent,
      type: a.type,
      freq: a.localizerFreq ?? '',
      prominent: a.prominent ?? false,
    };
    lineFeatures.push({
      type: 'Feature',
      properties: props,
      geometry: { type: 'LineString', coordinates: [faf, threshold] },
    });
    glideFeatures.push({
      type: 'Feature',
      properties: props,
      geometry: { type: 'LineString', coordinates: [threshold, gsEnd] },
    });
    fafFeatures.push({
      type: 'Feature',
      properties: { ident: a.fafIdent ?? 'FAF', runway: a.runwayIdent, prominent: a.prominent ?? false },
      geometry: { type: 'Point', coordinates: faf },
    });
  }

  lineSrc.setData({ type: 'FeatureCollection', features: lineFeatures });
  glideSrc.setData({ type: 'FeatureCollection', features: glideFeatures });
  fafSrc.setData({ type: 'FeatureCollection', features: fafFeatures });
}

/**
 * The actual 3D glide-slope descent ramp for hobbyist 3D mode: a line from
 * the runway threshold (ground) up to the FAF, lifted in screen space by
 * `raise` the same way an aircraft's own altitude stem is — so it reads as
 * a rising ramp under pitch/pan/zoom instead of the flat ground projection
 * `updateApproaches` draws for the always-on 2D view. FAF height comes from
 * each approach's own `glideSlopeDeg` (varies per runway — see
 * `glideSlopeDegFor` on the server, and the curated real-ILS entries in
 * client/src/data/approaches.ts), so steeper published/assumed procedures
 * read as visibly steeper ramps rather than one uniform angle everywhere.
 * Called every tick (like `updatePlanes`) so it tracks the live camera.
 */
export function updateGlideSlope3D(map: MlMap, approaches: ApproachProcedure[], threeD: boolean): void {
  const src = map.getSource(GLIDE_SLOPE_3D_SOURCE) as GeoJSONSource | undefined;
  if (!src) return;
  if (!threeD || map.getPitch() <= 0) {
    src.setData(empty());
    return;
  }

  const features: GeoJSON.Feature[] = [];
  for (const a of approaches) {
    const approachNm = a.approachNm ?? 7;
    const gsDeg = a.glideSlopeDeg ?? 3.0;
    const threshold: [number, number] = [a.thresholdLon, a.thresholdLat];
    const faf = finalApproachFix(a.thresholdLat, a.thresholdLon, a.courseDeg, approachNm);
    const fafAltFt = approachNm * NM_TO_FT * Math.tan((gsDeg * Math.PI) / 180);
    const raised = raise(map, faf, fafAltFt, false, true);
    if (raised[0] === faf[0] && raised[1] === faf[1]) continue;

    const color = a.prominent ? '#f5d0fe' : '#f0abfc';
    // Same fade-in-from-ground segmenting as the aircraft altitude stems
    // (STEM_SEGMENTS), so the ramp reads as rising out of the runway rather
    // than a single flat-opacity line.
    for (let i = 0; i < STEM_SEGMENTS; i++) {
      const t0 = i / STEM_SEGMENTS;
      const t1 = (i + 1) / STEM_SEGMENTS;
      const tMid = (t0 + t1) / 2;
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [threshold[0] + (raised[0] - threshold[0]) * t0, threshold[1] + (raised[1] - threshold[1]) * t0],
            [threshold[0] + (raised[0] - threshold[0]) * t1, threshold[1] + (raised[1] - threshold[1]) * t1],
          ],
        },
        properties: {
          color,
          opacity: (a.prominent ? 0.35 : 0.22) + (a.prominent ? 0.55 : 0.45) * tMid,
          width: 0.6 + 0.9 * tMid,
        },
      });
    }
  }
  src.setData({ type: 'FeatureCollection', features });
}
